import React, { useState, useEffect, useRef, createContext, useContext } from 'react';

export const OC = {
    orange: '#F97316',
    orangeHover: '#EA6C0A',
    orangeSubtle: '#FED7AA',
    orangeMuted: '#FFF7ED',
    charcoal: '#1C1C1E',
    success: '#22C55E',
    warning: '#F59E0B',
    urgent: '#EF4444',
    info: '#3B82F6',
    neutral: '#6B7280',
};

export const THEMES = {
    light: {
        bg: { page: '#FFFFFF', card: '#F9FAFB', subtle: '#F3F4F6', overlay: '#FFFFFF' },
        text: { primary: '#111827', secondary: '#6B7280', disabled: '#9CA3AF', onAccent: '#FFFFFF' },
        border: { base: '#E5E7EB', strong: '#D1D5DB' },
        shadow: { sm: '0 1px 2px rgba(17,24,39,.06)', md: '0 4px 12px rgba(17,24,39,.08)', lg: '0 12px 28px rgba(17,24,39,.10)' },
    },
    dark: {
        bg: { page: '#1C1C1E', card: '#2C2C2E', subtle: '#3A3A3C', overlay: '#1C1C1E' },
        text: { primary: '#F9FAFB', secondary: '#A1A1AA', disabled: '#71717A', onAccent: '#FFFFFF' },
        border: { base: '#3F3F46', strong: '#52525B' },
        shadow: { sm: '0 1px 2px rgba(0,0,0,.5)', md: '0 4px 16px rgba(0,0,0,.4)', lg: '0 18px 40px rgba(0,0,0,.5)' },
    },
};

export const CATEGORY = {
    jobs: { label: 'Jobs', color: '#3B82F6', soft: 'rgba(59,130,246,.14)', icon: 'briefcase' },
    internships: { label: 'Internships', color: '#8B5CF6', soft: 'rgba(139,92,246,.14)', icon: 'cap' },
    scholarships: { label: 'Scholarships', color: '#22C55E', soft: 'rgba(34,197,94,.14)', icon: 'award' },
    admissions: { label: 'Admissions', color: '#14B8A6', soft: 'rgba(20,184,166,.14)', icon: 'building' },
    events: { label: 'Events', color: '#F97316', soft: 'rgba(249,115,22,.14)', icon: 'spark' },
};

export const FONT = {
    display: '"Plus Jakarta Sans", system-ui, sans-serif',
    body: '"DM Sans", system-ui, sans-serif',
    mono: '"JetBrains Mono", ui-monospace, monospace',
};

export const CAT_KEYS = ['jobs', 'internships', 'scholarships', 'admissions', 'events'];

export const JOB_CATEGORIES = ['Engineering', 'Marketing', 'Design', 'Finance', 'Sales', 'HR', 'Operations', 'Product', 'Data Science'];

export const LOCATIONS = ['Karachi', 'Lahore', 'Islamabad', 'Dubai', 'Remote'];

export const EMPLOYMENT_TYPES = ['Full-time', 'Part-time', 'Contract', 'Internship', 'Freelance'];

export const WORKPLACE_TYPES = ['Remote', 'Hybrid', 'On-site'];

export const COMPANY_INDUSTRIES = [
    'Technology', 'Finance', 'Healthcare', 'Education', 'E-commerce',
    'Manufacturing', 'Consulting', 'Media', 'Real Estate', 'Transportation',
    'Hospitality', 'Retail', 'Energy', 'Telecommunications', 'Agriculture',
];

export const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'];

export const VERIFICATION_STATUS = {
    pending: { label: 'Pending Review', color: OC.warning, icon: 'clock' },
    verified: { label: 'Verified', color: OC.success, icon: 'check' },
    rejected: { label: 'Rejected', color: OC.urgent, icon: 'x' },
    featured: { label: 'Featured', color: OC.orange, icon: 'sparkle' },
};

export const STATUS_STEPS = ['applied', 'interview', 'offer'];

export const STATUS_META = {
    applied: { label: 'Applied', color: '#3B82F6' },
    review: { label: 'In review', color: '#8B5CF6' },
    interview: { label: 'Interview', color: '#F59E0B' },
    offer: { label: 'Offer', color: '#22C55E' },
    rejected: { label: 'Rejected', color: '#EF4444' },
    withdrawn: { label: 'Withdrawn', color: '#6B7280' },
    closed: { label: 'Closed', color: '#6B7280' },
};

export const DEFAULT_SETTINGS = {
    emailDigest: true,
    deadlineAlerts: true,
    whatsapp: false,
    telegram: true,
    newMatches: true,
    communityReplies: false,
    quietHours: true,
    compactCards: false,
};

export const DEFAULT_INTERESTS = ['jobs', 'scholarships'];

export const OC_LS_KEY = 'oc-proto-state-v1';

export function useTheme(theme) {
    return THEMES[theme] || THEMES.light;
}

export function urgency(daysLeft) {
    if (daysLeft == null || daysLeft < 0) {
        return { color: '#71717A', label: 'Closed', icon: '', tier: 'expired' };
    }
    if (daysLeft === 0) {
        return { color: OC.urgent, label: 'Closes today', icon: '🔴', tier: 'critical' };
    }
    if (daysLeft <= 3) {
        return { color: OC.urgent, label: `${daysLeft} days left — apply today`, icon: '🔴', tier: 'critical' };
    }
    if (daysLeft <= 7) {
        return { color: OC.orange, label: `${daysLeft} days left`, icon: '⚡', tier: 'urgent' };
    }
    if (daysLeft <= 14) {
        return { color: OC.warning, label: `${daysLeft} days left`, icon: '', tier: 'warning' };
    }
    return { color: 'secondary', label: `${daysLeft} days left`, icon: '', tier: 'normal' };
}

export function iconBtn(t) {
    return {
        width: 32,
        height: 32,
        borderRadius: 8,
        background: 'transparent',
        color: t.text.secondary,
        border: `1px solid ${t.border.base}`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
    };
}

export function ocLoad() {
    try {
        const data = localStorage.getItem(OC_LS_KEY);
        return data ? JSON.parse(data) : {};
    } catch (e) {
        return {};
    }
}

/** Map browser pathname to in-app route (used on first paint so deep links beat localStorage). */
export function routeFromPath(path = '') {
    const p = path || '/';
    if (p === '/' || p === '') return { name: 'home' };
    if (p === '/feed') return { name: 'feed' };
    if (p === '/search') return { name: 'search' };
    if (p === '/saved') return { name: 'saved' };
    if (p === '/applications') return { name: 'applications' };
    if (p === '/employers') return { name: 'employers' };
    if (p === '/post-job') return { name: 'postJob' };
    if (p === '/about') return { name: 'about' };
    if (p === '/account') return { name: 'account' };
    if (p === '/admin') return { name: 'admin' };
    if (p === '/community') return { name: 'community' };
    if (p === '/resources') return { name: 'resources' };
    if (p.startsWith('/resources/')) {
        const slug = p.split('/').filter(Boolean)[1];
        if (slug) return { name: 'guideDetail', slug };
    }
    if (p === '/help' || p === '/contact' || p === '/terms' || p === '/privacy' || p === '/guidelines') {
        const supportSlug = p.slice(1);
        return { name: 'support', supportSlug };
    }
    if (p === '/alerts') return { name: 'notifications' };
    if (p === '/company-profile') return { name: 'employers', tab: 'profile' };
    if (p.startsWith('/opportunity/')) {
        const id = p.split('/').filter(Boolean).pop();
        if (id) return { name: 'detail', id };
    }
    if (p.includes('/auth')) {
        const mode = new URLSearchParams(window.location.search).get('mode');
        return { name: mode === 'signup' ? 'signup' : 'login' };
    }
    return null;
}

export function matchQuery(op, q) {
    if (!q) return true;
    const hay = `${op.title} ${op.org} ${op.location} ${op.category} ${op.summary || ''}`.toLowerCase();
    return q.toLowerCase().split(/\s+/).every(w => hay.includes(w));
}

export function isEmployerRole(role) {
    return role === 'employer' || role === 'admin' || role === 'super_admin';
}

export function isAdminRole(role) {
    return role === 'admin' || role === 'super_admin';
}
